"use client"

import React, { useState, useEffect } from "react";
import { ListProps, Item } from "@/types";
import List from "./List";
import Link from "next/link";
import { useRouter, usePathname } from "next/navigation";

interface ExtendedItem extends Item {
  href: string;
}

interface ListWithSearchProps extends Omit<ListProps, 'items'> {
  items: ExtendedItem[];
  defaultSelectedIndex?: number | null;
}

const ListWithSearch: React.FC<ListWithSearchProps> = ({ items, onItemClick, defaultSelectedIndex = null }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(defaultSelectedIndex);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (pathname) {
      const index = items.findIndex(item => pathname.includes(item.href));
      setSelectedIndex(index !== -1 ? index : null);
    }
  }, [pathname, items]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setFilteredItems(
      items.filter(item =>
        item.text.toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredItems(items);
  };

  const handleItemClick = (item: ExtendedItem) => {
    if (onItemClick) {
      onItemClick(item);
    }
    router.push(item.href);
  };

  return (
    <>
      <div className="flex flex-row items-center">
        <label className="input input-bordered flex items-center gap-2 grow">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className="h-4 w-4 opacity-70"
          >
            <path
              fillRule="evenodd"
              d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
              clipRule="evenodd"
            />
          </svg>
          <input
            type="text"
            className="grow border-none bg-transparent lg:w-12 focus:ring-0"
            placeholder="Search"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </label>
        <button className="btn btn-ghost capitalize" onClick={handleClearSearch}>
          Clear
        </button>
      </div>
      <div className="w-full self-center">
        <List 
          items={filteredItems.map(item => ({
            ...item,
            component: <Link href={item.href}>{item.text}</Link>
          }))} 
          onItemClick={handleItemClick as (item: Item) => void}
          defaultSelectedIndex={selectedIndex} 
        />
      </div>
    </>
  );
};

export default ListWithSearch;
